import React from 'react';
import styled from 'styled-components';
import { useEditMode } from '../../shared/hooks/useEditMode';
import { useWorkViewStore } from '../../shared/store/WorkViewStore';
import { useWorkListActions } from '../../shared/hooks/useApi/useWorkList';
import { AlertPosition, AlertType, SelectType, TextAlignment, DimensionsType, WorkType, usesWorkDuration, WorkContentMode, SizeUnit, ServiceType } from '../../shared/enum/EnumRepository';
import { convertWorkSizeToString, getMainSizeData, hasSizeDataForUnit, hasValidMainSizeUnitForUpdate, normalizeWorkSizesForUpdate, SizeData, WorkData, WorkPropertyVisibleToggleField } from '../../shared/dto/EntityRepository';
import { UpdateWorkReq } from '../../shared/dto/ReqDtoRepository';
import { useImage } from '../../shared/hooks/useApi/useImage';
import UniversalIconBtn from '../../shared/component/headless/button/UniversalIconBtn';
import confirmIcon from '../../asset/icon/confirm2.png';
import confirmIcon2 from '../../asset/icon/confirm3.png';
import deleteCloseIcon from '../../asset/icon/delete_close.png';
import deleteOpenIcon from '../../asset/icon/delete_open.png';
import { WorkViewerInfo as WorkViewerInfoInput, WorkViewerTitle } from '../../shared/component/headless/input/InputBody';
import { NormalSmallTextArea, WorkViewerInfoTextArea } from '../../shared/component/headless/textarea/TextAreaBody';
import MoleculeInputDiv from '../../shared/component/molecule/MoleculeInputDiv';
import MoleculeTextAreaDiv from '../../shared/component/molecule/MoleculeTextAreaDiv';
import { useValidation } from '../../shared/hooks/useValidation';
import OrgDescription from '../../shared/component/organism/OrgDescription';
import { useWorkPropertyVisible } from '../../shared/hooks/useApi/useWorkPropertyVisible';
import OrgSelectDivVisi from '../../shared/component/organism/OrgSelectDivVisi';
import SizeInputWithMainUnit from '../work/SizeInputWithMainUnit';
import SizeInput from '../work/SizeInput';
import DimensionsTypeSelect from '../work/DimensionsTypeSelect';
import MoleculeDivToggle from '../../shared/component/molecule/MoleculeDivToggle';
import WorkTagInViewer from '../work/WorkTagInViewer';
import { getPageMessage } from '../../shared/constants/pageMessages';
import { useWorkTagStore } from '../../shared/store/workTagStore';
import { useWorkContentModeStore } from '../../shared/store/workContentModeStore';
import { getWorkTextFieldValue, isWorkEnglishShown } from '../../shared/util/workDisplayText';
import { useAuth } from '../../shared/hooks/useApi/useAuth';
import { usePageOwnerIdentityStore } from '../../shared/store/pageOwnerIdentityStore';
import { canShowWorkTagList } from '../../shared/util/visibility/workTagVisibility';
import {
  WORK_DESCRIPTION_MAX_LENGTH,
  DESCRIPTION_MAX_ROWS,
  INPUT_YEAR_MAX_LENGTH,
  INPUT_YEAR_MIN_LENGTH,
  INPUT_RUNNING_TIME_MIN_LENGTH,
  INPUT_RUNNING_TIME_MAX_LENGTH,
  INPUT_SIZE_TEXT_MIN_LENGTH,
  INPUT_SIZE_TEXT_MAX_LENGTH
} from '../../shared/constants/limits';
import { useStandardAlertStore } from '../../shared/store/portal/alertStore';
import { getAlertMessage } from '../../shared/constants/alertMessages';
import { useWorkIndexing } from '../../shared/hooks/useWorkIndexing';

const WorkViewerInfo: React.FC = () => {
  const { isEditMode } = useEditMode();
  const { updateWork, deleteWork, toggleWorkAllowDownload, toggleWorkAccess } = useWorkListActions();
  const activeWork = useWorkViewStore((s) => s.activeWork);
  const handleChange = useWorkViewStore((s) => s.updateActiveWork);
  const hasChanged = useWorkViewStore((s) => s.hasChanged);
  const imageChanged = useWorkViewStore((s) => s.imageChanged);
  const pendingImage = useWorkViewStore((s) => s.pendingImage);
  const isOverviewOpen = useWorkViewStore((s) => s.isOverviewOpen);
  const hasMultiSelected = useWorkViewStore((s) => s.multiSelectedWork.length > 0);
  const { uploadImage } = useImage();
  const { isIndexingMode } = useWorkIndexing();
  const { workPropertyVisible, updateWorkPropertyVisible } = useWorkPropertyVisible();
  const tags = useWorkTagStore((s) => s.tags);
  const listOwnerAui = useWorkTagStore((s) => s.listOwnerAui);
  const tagStatus = useWorkTagStore((s) => s.status);
  const { isOwner } = useAuth();
  const pageOwner = usePageOwnerIdentityStore((s) => s.pageOwner);
  const canShowTags = canShowWorkTagList({
    pageOwnerAui: pageOwner?.aui,
    listOwnerAui,
    status: tagStatus,
  });
  const visibleTags = canShowTags ? tags : [];
  const { checkType, isValidVideoUrl } = useValidation();
  const { enqueueAlert } = useStandardAlertStore();
  const workContentMode = useWorkContentModeStore((s) => s.workContentMode);
  const isWorkEng = workContentMode === WorkContentMode.ENG;

  if (hasMultiSelected) return null;
  if (isIndexingMode) {
    return (
      <WorkInfoContainer>
        <WorkInfoScroll>
          <WorkIndexingDescription>
            <EachDescriptionLine>
              {getPageMessage('WORK_INDEXING_HINT_SELECT_RIGHT')}
            </EachDescriptionLine>
            <EachDescriptionLine>
              ←
              {getPageMessage('WORK_INDEXING_HINT_DRAG_REORDER')}
            </EachDescriptionLine>
            <EachDescriptionLine>
              {getPageMessage('WORK_INDEXING_HINT_UNLISTED_KEEP_ORDER')}
            </EachDescriptionLine>
          </WorkIndexingDescription>
        </WorkInfoScroll>
      </WorkInfoContainer>
    );
  }
  if (!isOverviewOpen || !workPropertyVisible || !activeWork) return null;

  const engVisible = workPropertyVisible.engVisible !== false;
  const showEngLabel = isWorkEnglishShown(workContentMode, engVisible, isEditMode);
  const titleValue = getWorkTextFieldValue(
    activeWork, 'title', workContentMode, engVisible, isEditMode,
  );
  const materialValue = getWorkTextFieldValue(
    activeWork, 'material', workContentMode, engVisible, isEditMode,
  );
  const descriptionValue = getWorkTextFieldValue(
    activeWork, 'description', workContentMode, engVisible, isEditMode,
  );
  const sizeTextValue = activeWork.sizeText ?? '';
  const mainSize = getMainSizeData(activeWork.sizes, activeWork.mainSizeUnit);
  const convertedStringSize = convertWorkSizeToString(
    mainSize,
    activeWork.dimensionsType,
    sizeTextValue,
  );
  const sizeUnits = Object.values(SizeUnit);

  const handleChangeWithValidate = (
    field: keyof WorkData,
    value: string | DimensionsType | WorkType
  ) => {
    if (field !== 'dimensionsType' && field !== 'duration') {
      if (!checkType(field, value as string)) {
        return;
      };
    }
    handleChange({ [field]: value });
  }

  const handleSizeChange = (unit: SizeUnit, next: SizeData) => {
    const nextSize = { ...next, unit };
    const hasUnitSize = activeWork.sizes.some((size) => size.unit === unit);
    handleChange({
      sizes: hasUnitSize
        ? activeWork.sizes.map((size) => size.unit === unit ? nextSize : size)
        : [nextSize, ...activeWork.sizes],
    });
  };

  const handleMainSizeUnitChange = (unit: SizeUnit) => {
    if (!hasSizeDataForUnit(activeWork.sizes, unit)) {
      enqueueAlert({
        type: AlertType.ALERT,
        position: AlertPosition.TOP,
        content: getAlertMessage('SET_MAIN_SIZE_UNIT_REQUIRES_SIZE'),
      });
      return;
    }
    handleChange({
      mainSizeUnit: unit,
    });
  };

  const handleVisibleChange = async (field: WorkPropertyVisibleToggleField) => {
    if (!activeWork) return;
    if (!isEditMode) return null;
    await updateWorkPropertyVisible({
      ...workPropertyVisible,
      [field]: !workPropertyVisible[field]
    });
  };

  const handleToggleAllowDownload = async (value: boolean) => {
    if (!activeWork) return;
    const { uploadFile, artist: _artist, credit: _credit, ...workWithoutUploadFile } = activeWork;
    await toggleWorkAllowDownload({
      ...workWithoutUploadFile,
      tagIds: [],
      updateUploadFileReq: {
        originUrl: uploadFile.originUrl,
        uploadFileId: uploadFile.id,
        allowDownload: value,
        width: uploadFile.width,
        height: uploadFile.height,
      }
    });
  };

  const handleIsPublicToggle = async (value: boolean) => {
    if (!activeWork) return;
    if (!isEditMode) return;
    await toggleWorkAccess({
      id: activeWork.id,
      isPublic: value,
    });
  };

  const handleConfirm = async () => {
    if (!activeWork) return;
    const isVideo = usesWorkDuration(activeWork.workType);
    if (isVideo && activeWork.videoUrl && !isValidVideoUrl(activeWork.videoUrl)) return;
    const { uploadFile, artist: _artist, credit: _credit, ...workWithoutUploadFile } = activeWork;
    if (!hasValidMainSizeUnitForUpdate(activeWork.sizes, activeWork.mainSizeUnit)) {
      enqueueAlert({
        type: AlertType.ALERT,
        position: AlertPosition.TOP,
        content: getAlertMessage('SET_MAIN_SIZE_UNIT_REQUIRES_SIZE'),
      });
      return;
    }
    const normalizedSize = normalizeWorkSizesForUpdate(activeWork.sizes, activeWork.mainSizeUnit);
    const baseRequest: UpdateWorkReq = {
      ...workWithoutUploadFile,
      ...normalizedSize,
      updateUploadFileReq: {
        originUrl: uploadFile.originUrl,
        uploadFileId: uploadFile.id,
        allowDownload: uploadFile.allowDownload,
        width: pendingImage?.width ?? uploadFile.width,
        height: pendingImage?.height ?? uploadFile.height,
      },
      tagIds: activeWork.tags.map((t) => t.id),
      ...(isVideo
        ? { dimensionsType: DimensionsType.NONE }
        : { videoUrl: '', duration: '' }),
    };
    const finalRequest = imageChanged
      ? await uploadImage(ServiceType.WORK, baseRequest, pendingImage?.file)
      : baseRequest;
    if (!finalRequest) return;
    await updateWork(finalRequest as UpdateWorkReq);
  };

  const handleDelete = async () => {
    if (!activeWork) return;
    enqueueAlert({
      type: AlertType.CONFIRM,
      position: AlertPosition.TOP,
      content: getAlertMessage('CONFIRM_DELETE_WORK'),
      callBack: async () => {
        await deleteWork({ workId: activeWork.id });
      },
    });
  };

  return (
    <WorkInfoContainer>
      {isEditMode && (
        <EditActionsRail
          data-has-changed={hasChanged || imageChanged ? 'true' : 'false'}
        >
          {hasChanged && (
            <UniversalIconBtn
              isPrimary={true}
              size="fit"
              border={true}
              backgroundImg={confirmIcon}
              backgroundImgHover={confirmIcon2}
              onClick={handleConfirm}
            />
          )}
          <UniversalIconBtn
            size="fit"
            border={true}
            backgroundImg={deleteCloseIcon}
            backgroundImgHover={deleteOpenIcon}
            onClick={handleDelete}
          />
        </EditActionsRail>
      )}
      <WorkInfoScroll $hasEditActions={isEditMode}>
      <MoleculeInputDiv
        value={titleValue}
        defaultValue={"Title"}
        DefaultDiv={TitleDefaultDiv}
        placeholder={"Title"}
        handleChange={(e) =>
          handleChangeWithValidate(isWorkEng ? 'titleEn' : 'title', e.target.value)
        }
        inputStyle={WorkViewerTitle}
        StyledDiv={Title}
      />
      <WorkInfo>
        <WorkInfoRow>
          <WorkInfoLabel>Year</WorkInfoLabel>
          <MoleculeInputDiv
            value={activeWork.prodYear}
            defaultValue={"Year"}
            DefaultDiv={DefaultDiv}
            placeholder={"Year"}
            handleChange={(e) => handleChangeWithValidate("prodYear", e.target.value)}
            inputStyle={WorkViewerInfoWithLightBorder}
            StyledDiv={Info}
            minLimit={INPUT_YEAR_MIN_LENGTH}
            maxLimit={INPUT_YEAR_MAX_LENGTH}
          />
        </WorkInfoRow>
        <WorkInfoMaterial>
          <WorkInfoLabel>Material {showEngLabel ? '(en)' : ''}</WorkInfoLabel>
          <MoleculeTextAreaDiv
            value={materialValue}
            defaultValue={"Material"}
            DefaultDiv={DefaultDiv}
            placeholder={"Material"}
            handleChange={(e) =>
              handleChangeWithValidate(isWorkEng ? 'materialEn' : 'material', e.target.value)
            }
            textareaStyle={WorkViewerInfoTextAreaWithLightBorder}
            StyledDiv={Info}
          />
        </WorkInfoMaterial>
        {!usesWorkDuration(activeWork.workType) && (
          <WorkInfoSizes>
            <WorkInfoLabel>Size</WorkInfoLabel>
            <SizeContainer>
              {isEditMode ? (
                <>
                  <DimensionsTypeSelect
                    value={activeWork.dimensionsType}
                    onChange={(next) => handleChangeWithValidate('dimensionsType', next)}
                  />
                  <SizeInputContainer $dimensionsType={activeWork.dimensionsType}>
                  {activeWork.dimensionsType === DimensionsType.FIXED && sizeUnits.map((unit) => {
                    const size = activeWork.sizes.find((item) => item.unit === unit) ?? { width: '', height: '', unit };
                    return (
                      <SizeInputWithMainUnit
                        key={unit}
                        value={size}
                        onChange={(next) => handleSizeChange(unit, next)}
                        StyledDiv={Info}
                        isMainUnit={activeWork.mainSizeUnit === unit}
                        onMainUnitChange={() => handleMainSizeUnitChange(unit)}
                      />
                    );
                  })}
                  {activeWork.dimensionsType === DimensionsType.CUSTOM && (
                    <MoleculeInputDiv
                      value={sizeTextValue}
                      defaultValue={"Custom size"}
                      DefaultDiv={DefaultDiv}
                      placeholder={"Custom size"}
                      handleChange={(e) => handleChangeWithValidate('sizeText', e.target.value)}
                      inputStyle={WorkViewerInfoWithLightBorder}
                      StyledDiv={Info}
                      minLimit={INPUT_SIZE_TEXT_MIN_LENGTH}
                      maxLimit={INPUT_SIZE_TEXT_MAX_LENGTH}
                    />
                  )}
                  </SizeInputContainer>

                </>
              ) : (
                <SizeInput
                  value={mainSize}
                  onChange={(next) => handleSizeChange(activeWork.mainSizeUnit, next)}
                  StyledDiv={Info}
                  dimensionsType={activeWork.dimensionsType}
                  displayValue={convertedStringSize}
                />
              )}
            </SizeContainer>
          </WorkInfoSizes>
        )}
        {usesWorkDuration(activeWork.workType) && (
          <WorkInfoRow>
            <WorkInfoLabel>Duration</WorkInfoLabel>
            <MoleculeInputDiv
              value={activeWork.duration}
              defaultValue={"00:00:00"}
              DefaultDiv={DefaultDiv}
              placeholder={"00:00:00 (hh:mm:ss)"}
              handleChange={(e) => handleChangeWithValidate("duration", e.target.value)}
              inputStyle={WorkViewerInfoWithLightBorder}
              StyledDiv={Info}
              minLimit={INPUT_RUNNING_TIME_MIN_LENGTH}
              maxLimit={INPUT_RUNNING_TIME_MAX_LENGTH}
            />
          </WorkInfoRow>
        )}
        {(workPropertyVisible.workType || isEditMode) && (
          <WorkInfoRow>
            <WorkInfoLabel>Type</WorkInfoLabel>
            <OrgInputDivVisiWrapper>
              <OrgSelectDivVisi
                value={activeWork.workType || WorkType.NONE}
                selectType={SelectType.WORK_TYPE}
                handleChange={(value) => handleChangeWithValidate("workType", value)}
                StyledDiv={Info}
                visible={workPropertyVisible.workType}
                changeVisible={() => handleVisibleChange('workType')}
              />
            </OrgInputDivVisiWrapper>
          </WorkInfoRow>
        )}
        {(isEditMode || (activeWork.tags?.length > 0)) && (
        <WorkInfoTags>
            <WorkInfoLabel>Label</WorkInfoLabel>
            <TagContainer>
              {visibleTags.map((tag) =>
                  <WorkTagInViewer key={tag.id} tag={tag} />
              )}
            </TagContainer>
        </WorkInfoTags>
        )}
      </WorkInfo>
      <OrgDescriptionWrapper>
        <OrgDescription
          value={descriptionValue}
          handleChange={(e) =>
            handleChangeWithValidate(isWorkEng ? 'descriptionEn' : 'description', e.target.value)
          }
          alignment={TextAlignment.LEFT}
          StyledTextarea={NormalSmallTextArea}
          StyledDescription={Description}
          visible={workPropertyVisible.description}
          changeVisible={() => handleVisibleChange('description')}
          maxLength={WORK_DESCRIPTION_MAX_LENGTH}
          maxRows={DESCRIPTION_MAX_ROWS}
        />
      </OrgDescriptionWrapper>
      {isEditMode && (
        <WorkAccessToggles>
          {isOwner && (
            <MoleculeDivToggle
              value={activeWork.isPublic}
              name="Public"
              handleToggle={(e) => handleIsPublicToggle(e.target.checked)}
            />
          )}
          {activeWork.uploadFile.originUrl && !usesWorkDuration(activeWork.workType) &&
            <MoleculeDivToggle
              value={activeWork.uploadFile.allowDownload}
              name={getPageMessage('DOWNLOAD_TOGGLE_LABEL')}
              handleToggle={(e) => handleToggleAllowDownload(e.target.checked)}
            />
          }
        </WorkAccessToggles>
      )}
      </WorkInfoScroll>
    </WorkInfoContainer>
  );
}

const WorkInfoContainer = styled.div`
  position: relative;
  width: clamp(400px, 30vw, 500px);
  display: flex;
  flex-direction: column;
  flex: 0 1 auto;
  min-height: 0;
  padding-top: 13vh;
  box-sizing: border-box;
`

const WorkInfoScroll = styled.div<{ $hasEditActions?: boolean }>`
  min-height: 0;
  flex: 1 1 auto;
  overflow-y: auto;
  padding-right: ${({ $hasEditActions }) => ($hasEditActions ? '32px' : '0')};
  box-sizing: border-box;
  display: flex;
  flex-direction: column;

  &::-webkit-scrollbar {
    display: none;
  }
`

const WorkIndexingDescription = styled.div`
  width: 100%;
  height: fit-content;
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const EachDescriptionLine = styled.div`
  width: 100%;
  height: fit-content;
  display: flex;
  white-space: pre-line;
  ${({ theme }) => theme.typography.Body_04};
  color: ${({ theme }) => theme.colors.color_Gray_04};
`;

const Title = styled.h2`
  width: 100%;
  height: fit-content;
  min-height: 28px;

  display: flex;

  padding: 4px 0 10px 0;
  color: ${({ theme }) => theme.colors.color_Gray_03};
  ${({ theme }) => theme.typography.Body_03_1};
`;

const TitleDefaultDiv = styled.div`
  width: 100%;

  display: flex;
  align-items: center;

  margin-bottom: 10px;
  color: ${({ theme }) => theme.colors.color_Gray_05} !important;
  ${({ theme }) => theme.typography.Body_03_1};

  background-color: ${({ theme }) => theme.colors.color_Gray_06};
`;


const WorkInfo = styled.div`
  display: flex;
  flex-direction: column;
  padding-right: 2px;
  gap: 6px;
  margin-bottom: 12px;
  color: ${({ theme }) => theme.colors.color_Gray_03};
`;

const WorkViewerInfoWithLightBorder = styled(WorkViewerInfoInput)`
  border-bottom: 0.5px solid ${({ theme }) => theme.colors.color_Gray_04};
`;

const WorkViewerInfoTextAreaWithLightBorder = styled(WorkViewerInfoTextArea)`
  border-bottom: 0.5px solid ${({ theme }) => theme.colors.color_Gray_04};
`;

const OrgInputDivVisiWrapper = styled.div`
  width: 100%;
  display: flex;
  align-items: center;
`
const WorkInfoSizes = styled.div`
  height: fit-content;
  display: flex;
  align-items: flex-start;
  gap: 8px;
  ${({ theme }) => theme.typography.Body_04};
`;

const WorkInfoMaterial = WorkInfoSizes;

const WorkInfoTags = styled.div`
  height: fit-content;
  display: flex;
  align-items: flex-start;
  gap: 8px;
  ${({ theme }) => theme.typography.Body_04};
`;

const TagContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`

const Info = styled.div`
  flex: 1 1 auto;            /* 남은 공간 채움 */
  min-width: 0;              /* flex item 줄바꿈 허용 */
  text-align: left;          /* 읽기 쉽게 왼쪽 정렬 */
  white-space: normal;       /* 기본 줄바꿈 허용 */
  word-break: break-word;    /* 긴 단어 쪼개기 */
  overflow-wrap: anywhere;   /* 아주 긴 연속 문자열 강제 줄바꿈 */
  line-height: 1.2;          /* 여러 줄일 때 겹치지 않게 */
`;

const OrgDescriptionWrapper = styled.div`
  width: 100%;
`;

const DefaultDiv = styled.div`
  width: fit-content;
  color: ${({ theme }) => theme.colors.color_Gray_05} !important;
  ${({ theme }) => theme.typography.Body_04};

  background-color: ${({ theme }) => theme.colors.color_Gray_06};
`;

const Description = styled.div`
  padding: 20px 0 24px 0;
  line-height: 1.2;
  color: ${({ theme }) => theme.colors.color_Gray_03};
  ${({ theme }) => theme.typography.Body_04};
`

const WorkInfoRow = styled.div`
  height: 18px;
  min-height: 18px;
  margin-bottom: 1px;
  display: flex;
  align-items: center;
  gap: 8px;
  ${({ theme }) => theme.typography.Body_04};
`;

const WorkInfoLabel = styled.span`
  flex-shrink: 0;
  min-width: 70px;
  padding-top: 2px;
  color: ${({ theme }) => theme.colors.color_Gray_03};
  font-weight: 500;
`;

const WorkAccessToggles = styled.div`
  display: flex;
  flex-direction: column;
  padding: 8px 0 4px;
`;

const EditActionsRail = styled.div`
  position: absolute;
  top: calc(13vh + 4px);
  right: 0;
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 0.5vh;
  z-index: 2;
`;

const SizeContainer = styled.div`
  flex: 1 1 auto;
  min-width: 0;
  display: flex;
  flex-direction: column;
`;

const SizeInputContainer = styled.div<{ $dimensionsType: DimensionsType }>`
  display: flex;
  flex-direction: column;
  gap: 4px;
  height: ${({ $dimensionsType }) => {
    if ($dimensionsType === DimensionsType.FIXED) return '90px';
    if ($dimensionsType === DimensionsType.CUSTOM) return '24px';
    return '0px';
  }};
  margin-top: 4px;
  overflow: hidden;
  transition: height 0.5s ease-in-out;
`;

export default WorkViewerInfo;
